import { useState } from 'react';
import moment from 'moment';
import { yupToFormErrors } from 'formik';
import * as Yup from 'yup';

import { useOrderStore } from 'store/orderStore';
import { useCampaignStore } from 'store/campaignStore';

import {
  BidMode,
  ManualKeywordProperty,
} from 'core/rtbCampaign/singlePage/keyword';
import {
  SelectPattern,
  RunningPattern,
  BudgetType,
  ActionType,
} from 'core/rtbCampaign/singlePage/common';
import { useBidRange } from './useBidRange';

interface initialKeywords {
  defaultForm: any;
  newForm: any;
}

const maxDayRange = 92;
const forbiddenKeywords = [
  'PChome',
  '網家',
  '蝦皮',
  'shopee',
  'Coupang',
  '酷澎',
];

const useKeywordValidation = (
  initialState: initialKeywords = {
    defaultForm: {},
    newForm: {},
  },
) => {
  const { defaultForm, newForm } = initialState;
  const { order } = useOrderStore();

  const [errors, setErrors] = useState<any>({ basic: {}, products: '' });
  const { actionType, roasList } = useCampaignStore();
  const bidRange = useBidRange();

  const campaignBudgetMinimum = order.campaignConstraint.campaignBudgetMinimum;
  const customRoasMAx = () => {
    const baseValue = roasList[1];
    if (baseValue === '-') return 50;
    const computed =
      Math.round((Number(baseValue) + Number.EPSILON) * 1.5 * 100) / 100;
    return computed > 50 ? 50 : computed;
  };

  const validationSchema = Yup.object().shape({
    basic: Yup.object().shape({
      advertiserId: Yup.number().required('這裡還是空白的哦！'),
      orderId: Yup.number().required('這裡還是空白的哦！'),
      name: Yup.string().required('這裡還是空白的哦！'),
      startDate: Yup.date().when('runningPattern', {
        is: (runningPattern: string) =>
          runningPattern === RunningPattern.LIMITED,
        then: (schema) =>
          schema.test('min-date', '開始時間不能早於當下時間', (startDate) => {
            const alreadyRunning =
              actionType === ActionType.EDIT &&
              new Date(defaultForm.basic.startDate) <= new Date();

            if (alreadyRunning) {
              return true;
            }

            // 判斷是否為今天
            if (
              moment(moment(startDate).startOf('day')).isSame(
                moment().startOf('day'),
              )
            ) {
              return true;
            }
            return (
              moment(startDate).isSame(moment()) ||
              moment(startDate).isAfter(moment())
            );
          }),
      }),
      endDate: Yup.date().when('runningPattern', {
        is: (runningPattern: string) =>
          runningPattern === RunningPattern.LIMITED,
        then: (schema) =>
          schema
            .test(
              'max-date',
              `請選擇不超出${maxDayRange}天的時間區間`,
              (endDate, { parent }) =>
                moment(endDate).diff(parent.startDate, 'seconds') <=
                maxDayRange * 24 * 60 * 60,
            )
            .test('max-date', '無法選擇過去時間為結束時間', (endDate) =>
              moment(endDate).isAfter(moment()),
            ),
      }),
      selectPattern: Yup.string().required('這裡還是空白的哦！'),
      runningPattern: Yup.string().required('這裡還是空白的哦！'),
      budgetType: Yup.string().required('這裡還是空白的哦！'),
      bidMode: Yup.string().required('這裡還是空白的哦！'),
      roasOption: Yup.object().shape({
        roasStrategy: Yup.string()
          .nullable()
          .when('bidMode', {
            is: (bidMode: string) => bidMode === BidMode.AUTO,
            then: (schema) => schema.required('這裡還是空白的哦！'),
          }),
        roas: Yup.number()
          .nullable()
          .transform((value) => (Number.isNaN(value) ? null : value))
          .when('roasStrategy', {
            is: (roasStrategy: string) => roasStrategy === 'CUSTOM_ROAS',
            then: (schema) =>
              schema
                .required('這裡還是空白的哦！')
                .test('min', '自訂投入產出比需大於 1', (value) => {
                  const isValid = value !== undefined && value !== null;
                  if (isValid) {
                    const isValidValue = value >= 1;
                    return isValidValue;
                  }
                  return true;
                })
                .test(
                  'max',
                  `自訂投入產出比不可高於 ${customRoasMAx()}`,
                  (value) => {
                    const isValid = value !== undefined && value !== null;

                    if (isValid) {
                      const isValidValue = value <= customRoasMAx();
                      return isValidValue;
                    }
                    return true;
                  },
                )
                .test(
                  'is-toFixed-2',
                  `投入產出比僅支援小數點後兩位`,
                  (value) => {
                    const regex = /^-?\d+(\.\d{0,2})?$/;
                    if (value === null) {
                      return true;
                    } else {
                      return (
                        value !== undefined && regex.test(value.toString())
                      );
                    }
                  },
                ),
          }),
      }),
      budget: Yup.number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .test('is-toFixed-2', `預算僅支援小數點後兩位`, (value) => {
          const regex = /^-?\d+(\.\d{0,2})?$/;
          if (value === null) {
            return true;
          } else {
            return value !== undefined && regex.test(value.toString());
          }
        })
        .when(['runningPattern', 'budgetType'], {
          is: (runningPattern, budgetType) =>
            runningPattern === RunningPattern.UNLIMITED &&
            budgetType === BudgetType.DAILY,
          then: (schema) =>
            schema
              .required('這裡還是空白的哦！')
              .test(
                'is-positive',
                `增加的日預算已超過訂單剩餘可支配預算：${order.currency} ${order.budgetBalance}`,
                (value) => {
                  const totalBudget = order.budgetBalance;
                  const remainBudget = totalBudget - (value ?? 0);
                  switch (actionType) {
                    case ActionType.CREATE:
                    case ActionType.COPY:
                      if (remainBudget < 0) {
                        return false;
                      }
                      return true;
                    case ActionType.EDIT:
                      const remainBudgetEdit =
                        totalBudget - (value ?? 0) + defaultForm.basic.budget;
                      if (value === defaultForm.basic.budget) {
                        return true;
                      }
                      if (remainBudgetEdit < 0) {
                        return false;
                      }
                      return true;
                    default:
                      return value <= order.budgetBalance;
                  }
                },
              )
              .test(
                'is-positive',
                `總預算預估為：${order.currency} ${newForm.basic.budget}，不可低於預期花費${order.currency} ${defaultForm.basic.expectedSpent}`,
                (value) => value >= defaultForm.basic.expectedSpent,
              ),
        })
        .when(['runningPattern', 'budgetType'], {
          is: (runningPattern, budgetType) =>
            runningPattern === RunningPattern.LIMITED &&
            budgetType === BudgetType.DAILY,
          then: (schema) =>
            schema
              .required('這裡還是空白的哦！')
              .test(
                'is-positive',
                `預算不可低於廣告活動預算最小值${campaignBudgetMinimum}`,
                (value) =>
                  value !== undefined &&
                  value !== null &&
                  value >= campaignBudgetMinimum,
              )
              .test(
                'is-positive',
                `已超過總共可支配預算：${order.currency} ${order.budgetBalance}`,
                (value) => {
                  const totalBudget = order.budgetBalance;
                  const remainBudget = totalBudget - (value ?? 0);
                  switch (actionType) {
                    case ActionType.CREATE:
                    case ActionType.COPY:
                      if (remainBudget < 0) {
                        return false;
                      }
                      return true;
                    case ActionType.EDIT:
                      const remainBudgetEdit =
                        totalBudget - (value ?? 0) + defaultForm.basic.budget;
                      if (value === defaultForm.basic.budget) {
                        return true;
                      }
                      if (remainBudgetEdit < 0) {
                        return false;
                      }
                      return true;
                    default:
                      return (
                        value !== undefined &&
                        value !== null &&
                        value <= order.budgetBalance
                      );
                  }
                },
              )
              .test(
                'is-positive',
                `不可低於預期花費：${order.currency} ${defaultForm.basic.expectedSpent}`,
                (value) =>
                  value !== undefined &&
                  value !== null &&
                  value >= defaultForm.basic.expectedSpent,
              ),
        })
        .when('budgetType', {
          is: (val: string) => val === BudgetType.TOTAL,
          then: (schema) =>
            schema
              .required('這裡還是空白的哦！')
              .test(
                'is-positive',
                `預算不可低於廣告活動預算最小值${campaignBudgetMinimum}`,
                (value) =>
                  value !== undefined &&
                  value !== null &&
                  value >= campaignBudgetMinimum,
              )
              .test(
                'is-positive',
                `已超過總共可支配預算：${order.currency} ${order.budgetBalance}`,
                (value) => {
                  const totalBudget = order.budgetBalance;
                  const remainBudget = totalBudget - (value ?? 0);
                  switch (actionType) {
                    case ActionType.CREATE:
                    case ActionType.COPY:
                      if (remainBudget < 0) {
                        return false;
                      }
                      return true;
                    case ActionType.EDIT:
                      const remainBudgetEdit =
                        totalBudget - (value ?? 0) + defaultForm.basic.budget;
                      if (value === defaultForm.basic.budget) {
                        return true;
                      }
                      if (remainBudgetEdit < 0) {
                        return false;
                      }
                      return true;
                    default:
                      return value !== undefined && value !== null
                        ? value <= order.budgetBalance
                        : true;
                  }
                },
              )
              .test(
                'is-positive',
                `不可低於預期花費：${order.currency} ${defaultForm.basic.expectedSpent}`,
                (value) => value >= defaultForm.basic.expectedSpent,
              ),
        }),

      outboundInfo: Yup.object().when('enableOutbound', {
        is: (enableOutbound) => {
          return enableOutbound;
        },
        then: (schema) =>
          schema.shape({
            brandNo: Yup.string().test(
              'brand-no-required-if-default',
              '品牌為必填欄位',
              function (value) {
                // Access defaultForm from closure or context

                if (defaultForm.basic?.outboundInfo?.brandNo?.length > 0) {
                  return true;
                }
                // Otherwise, skip required validation.
                return Boolean(value && value.length > 0);
              },
            ),
            goal: Yup.string()
              .required('投放目標為必填欄位')
              .oneOf(['LINK_CLICKS', 'CONVERSIONS'], '投放目標為必填欄位'),
            adContent: Yup.string()
              .required('廣告內容為必填欄位')
              .max(80, '廣告內容不能超過 80 個字元')
              .test(
                'no-links',
                '廣告內容不能包含可連結網址',
                (value) => !/(www|http:\/\/|https:\/\/)/.test(value),
              )
              .test(
                'no-forbidden-keywords',
                '廣告內容不能包含禁用的關鍵字',
                (value) =>
                  !forbiddenKeywords.some((keyword) =>
                    new RegExp(keyword, 'i').test(value),
                  ),
              ),
          }),
      }),
      dailyTargetBudget: Yup.number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .test('is-toFixed-2', `預算僅支援小數點後兩位`, (value) => {
          const regex = /^-?\d+(\.\d{0,2})?$/;
          if (value === null) {
            return true;
          } else {
            return value !== undefined && regex.test(value.toString());
          }
        })
        .when(['runningPattern', 'budgetType'], {
          is: (runningPattern, budgetType) =>
            runningPattern === RunningPattern.UNLIMITED &&
            budgetType === BudgetType.DAILY,
          then: (schema) =>
            schema
              .required('這裡還是空白的哦！')
              .test(
                'is-positive',
                `預算不可低於廣告活動預算最小值${campaignBudgetMinimum}`,
                (value) => value >= campaignBudgetMinimum,
              ),
        })
        .when(['runningPattern', 'budgetType'], {
          is: (runningPattern, budgetType) =>
            runningPattern === RunningPattern.LIMITED &&
            budgetType === BudgetType.DAILY,
          then: (schema) =>
            schema
              .required('這裡還是空白的哦！')
              .test(
                'is-positive',
                `預算不可低於廣告活動預算最小值${campaignBudgetMinimum}`,
                (value) => value >= campaignBudgetMinimum,
              ),
        })
        .when(['runningPattern', 'budgetType', 'enableOutbound'], {
          is: (runningPattern, budgetType, enableOutbound) =>
            runningPattern === RunningPattern.LIMITED &&
            budgetType === BudgetType.DAILY &&
            enableOutbound === true,
          then: (schema) =>
            schema.test(
              'min-daily-target-budget',
              `已開啟聯播網，日預算無法調整至 200 以下`,
              (value) => (value ?? 0) >= 200,
            ),
        })
        .when(['runningPattern', 'budgetType', 'enableOutbound'], {
          is: (runningPattern, budgetType) =>
            runningPattern === RunningPattern.LIMITED &&
            budgetType === BudgetType.DAILY &&
            defaultForm.basic?.outboundInfo?.dailyBudget !== undefined &&
            newForm.basic?.enableOutbound === false,
          then: (schema) =>
            schema.test(
              'min-daily-target-budget',
              `不可低於已分配日預算`,
              (value) => {
                return (
                  (value ?? 0) > defaultForm.basic?.outboundInfo?.dailyBudget
                );
              },
            ),
        }),
    }),
    products: Yup.array().when('basic.selectPattern', {
      is: (selectPattern) => selectPattern === SelectPattern.MANUAL,
      then: (schema) =>
        schema
          .min(1, '尚未選取商品')
          .max(25, '已超過選品上限25品，請重新調整選品'),
    }),
    autoProducts: Yup.array().when('basic.selectPattern', {
      is: (selectPattern) => selectPattern === SelectPattern.AUTO,
      then: (schema) => schema.min(1, '該品牌目前無適用商品，請選擇其他品牌'),
    }),
    keywords: Yup.array()
      .when('basic.bidMode', {
        is: (bidMode) => bidMode === BidMode.MANUAL,
        then: (schema) =>
          schema.min(1, '手動關鍵字為空').max(1000, '手動關鍵字超過1000個'),
      })
      .when('basic.bidMode', {
        is: (bidMode) => bidMode === BidMode.MANUAL,
        then: (schema) =>
          schema.of(
            Yup.object({
              [ManualKeywordProperty.PRICE]: Yup.number()
                .max(bidRange[1], `關鍵字出價須低於 ${bidRange[1]} 元`)
                .moreThan(0, `金額請高於 0 元以上哦!`),
            }),
          ),
      }),
    limitations: Yup.object()
      .shape({
        include: Yup.array().of(Yup.object().shape({ value: Yup.array() })),
      })
      .when('basic.selectPattern', (selectPattern, schema) =>
        selectPattern[0] === SelectPattern.AUTO &&
        newForm.basic.bidMode === BidMode.AUTO
          ? Yup.object()
              .shape({
                include: Yup.array().of(
                  Yup.object().shape({ value: Yup.array() }),
                ),
              })
              .test(
                'is-positive',
                '您的智能選品未有推薦關鍵字，請選擇其他品牌或改用手動選品',
                (value) =>
                  value?.include?.some(
                    (include) => include.value && include.value.length > 0,
                  ),
              )
          : schema,
      )
      .when('basic.selectPattern', (selectPattern, schema) =>
        selectPattern[0] === SelectPattern.MANUAL &&
        newForm.basic.bidMode === BidMode.AUTO
          ? Yup.object()
              .shape({
                include: Yup.array().of(
                  Yup.object().shape({ value: Yup.array() }),
                ),
              })
              .test(
                'is-positive',
                '您的手動選品未有推薦關鍵字，請選擇其他商品',
                (value) =>
                  value?.include?.some(
                    (include) => include.value && include.value.length > 0,
                  ),
              )
          : schema,
      ),
  });

  // 即時驗證表單
  const handleValidation = () => {
    validationSchema
      .validate(newForm, { abortEarly: false })
      .then(function () {
        setErrors({});
      })
      .catch(function (err) {
        // console.log('err', err);
        setErrors(yupToFormErrors(err));
        console.log('errors', yupToFormErrors(err));
      });
  };

  return {
    validationSchema,
    errors,
    setErrors,
    handleValidation,
  };
};

export default useKeywordValidation;
